import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { triggerChartRedraw, useTitle } from 'src/utility'
import { ResizableLayout } from 'src/components/ui'
import * as events from 'src/utility/events'
import { useSite } from 'src/contexts/site'
import { ForecastsTab } from './tabs'
import { ChartList } from './components'

const serialize = (x: string[]): string => x.join(',')
const deserialize = (x: string | null): string[] => (x ? x.split(',') : [])

export function ForecastPage(): JSX.Element {
  useTitle('Forecast')
  const [searchParams, setSearchParams] = useSearchParams()
  const { id } = useSite()

  useEffect(() => {
    events.forecastPageViewed({ fid: id })
  }, [id])

  const activeModels = useMemo(
    () => deserialize(searchParams.get('models')),
    [searchParams],
  )

  const addModel = (modelId: string): void => {
    events.forecastPageModelAdded({
      fid: id,
      modelId,
    })
    setSearchParams(params => {
      params.set('models', serialize([...activeModels, modelId]))
      return params
    })
  }

  const removeModel = (modelId: string): void => {
    events.forecastPageModelRemoved({
      fid: id,
      modelId,
    })
    setSearchParams(params => {
      const filtered = activeModels.filter(id => id !== modelId)
      if (filtered.length > 0) {
        params.set('models', serialize(filtered))
      } else {
        params.delete('models')
      }
      return params
    })
  }

  useEffect(() => {
    triggerChartRedraw()
  }, [activeModels])

  return (
    <ResizableLayout
      collapsible
      leftComponent={
        <ForecastsTab
          activeModels={activeModels}
          addModel={addModel}
          removeModel={removeModel}
        />
      }
      rightComponent={
        <ChartList
          activeModels={activeModels}
          removeModel={removeModel}
          className="max-h-[calc(100vh-57px)]"
        />
      }
      minSize={500}
      defaultSize={30}
      onResize={triggerChartRedraw}
    />
  )
}
